import { useLoad } from "@redotech/react-util/load";
import { IntegrationKind } from "@redotech/redo-integrations-core";
import { Integration } from "@redotech/redo-model/advanced-flow/integrations";
import { toast } from "@redotech/redo-web/alert";
import { useContext } from "react";
import { RedoIntegrationsRpcClientContext } from "../integrations-rpc-client-provider";
import { RedoMarketingRpcClientContext } from "../marketing-rpc-client-provider";

const DEFAULT_ENABLED_INTEGRATIONS = Object.fromEntries(
  Object.values(Integration).map((k) => [k, false]),
) as Record<Integration, boolean>;

const INTEGRATION_KIND_MAP: Partial<Record<Integration, IntegrationKind>> = {
  [Integration.YOTPO]: IntegrationKind.Yotpo,
  [Integration.LOYALTY_LION]: IntegrationKind.LoyaltyLion,
};

export function useEnabledIntegrations() {
  const marketingRpcClient = useContext(RedoMarketingRpcClientContext);
  const integrationsRpcClient = useContext(RedoIntegrationsRpcClientContext);

  const enabledIntegrationsLoad = useLoad(async () => {
    if (!marketingRpcClient) {
      return DEFAULT_ENABLED_INTEGRATIONS;
    }
    try {
      const result: Record<Integration, boolean> = {
        ...DEFAULT_ENABLED_INTEGRATIONS,
      };

      // Check integrations in the new framework (Yotpo, LoyaltyLion) with a single call
      // These are the integrations from the Integration Mananger Framework
      if (integrationsRpcClient) {
        const integrationKinds = Object.values(INTEGRATION_KIND_MAP);
        const { integrations } =
          await integrationsRpcClient.getAvailableIntegrations({
            integrationKinds,
          });

        for (const [integration, kind] of Object.entries(
          INTEGRATION_KIND_MAP,
        )) {
          const data = integrations[kind];
          if (data) {
            result[integration as Integration] = Object.values(
              data.connections,
            ).some((c) => c.isActive);
          }
        }
      }

      // Recharge is still on the old integration framework
      const rechargeResult =
        await marketingRpcClient.getRechargeIntegration(null);
      result[Integration.RECHARGE] = rechargeResult?.exists ?? false;

      return result;
    } catch {
      toast("Failed to load integration settings", { variant: "error" });
      return DEFAULT_ENABLED_INTEGRATIONS;
    }
  }, [marketingRpcClient, integrationsRpcClient]);

  const value = enabledIntegrationsLoad.value ?? DEFAULT_ENABLED_INTEGRATIONS;
  return Object.values(value).some(Boolean)
    ? value
    : DEFAULT_ENABLED_INTEGRATIONS;
}
